import { OnInit, Component } from '@angular/core';
import { LazyLoadEvent, MessageService, SelectItem } from 'primeng';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { CustomerService, LoggerService } from '../../services';
import { TaggedLogger } from '../../services';
import _ from 'lodash';
import { UserAutoDeactivationAuditDTO } from '../../interfaces/customer-user-deactivation/user-auto-deactivation-audit.dto.interface';
import { IOrganisationSubtype } from '../../interfaces/customer-user-deactivation/organisation-subtype.dto.interface';
import { Message } from '../../classes/Message';

export enum AuditType {
  All,
  Warned,
  Deactivated,
}

@Component({
  selector: 'customer-user-deactivation-admin',
  templateUrl: './customer-user-deactivation-admin.component.html',
  styleUrls: ['./customer-user-deactivation-admin.component.css'],
})
export class CustomerUserDeactivationAdminComponent implements OnInit {
  constructor(
    private customerService: CustomerService,
    private formBuilder: UntypedFormBuilder,
    private loggerService: LoggerService,
    private messageService: MessageService,
  ) {}

  private logger: TaggedLogger;
  deactivationSettings: IOrganisationSubtype[];
  selectedSetting: any;
  form: UntypedFormGroup;
  ctrls: { [p: string]: AbstractControl };

  usersData: UserAutoDeactivationAuditDTO[] = [];
  viewData: UserAutoDeactivationAuditDTO[] = [];
  filteredData: UserAutoDeactivationAuditDTO[] = [];

  showSettingDialog = false;

  // one for each datatable
  first: number;
  numberOfRows: number;
  searchCustomer: string;
  searchUser: string;

  auditTypeOptions: SelectItem[] = [];
  selectedAuditType = AuditType.All;
  csvDownloading = false;

  async ngOnInit() {
    this.logger = this.loggerService.taggedLogger('Customer User Deactivation Admin');

    this.auditTypeOptions = [
      { label: 'All', value: AuditType.All },
      { label: 'Warned Only', value: AuditType.Warned },
      { label: 'Deactivated Only', value: AuditType.Deactivated },
    ];

    this.form = this.formBuilder.group({
      accessWarnDays: new UntypedFormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(10000),
      ]),
      accessDeactivateDays: new UntypedFormControl('', [
        Validators.required,
        Validators.min(1),
        Validators.max(365),
      ]),
    });

    this.ctrls = this.form.controls;

    this.loadDeactivationSettings();

    await this.customerService.getDeactivatedUsers().then((result) => {
      this.usersData = this.usersData.concat(result);

      //this.logger.trace('Got deactivated users', result);
    });

    await this.customerService.getWarnedUsers().then((result) => {
      this.usersData = this.usersData.concat(result);

      //this.logger.trace('Got pre-warned users', result);
    });

    this.usersData = _.orderBy(
      this.usersData,
      // sort by username then whether they have a warning date
      ['userName', (user) => !!user.warnDate],
      ['asc', 'desc'],
    );

    this.first = 0;
    this.numberOfRows = 20;

    this.setPageData();
  }

  letUser(item: UserAutoDeactivationAuditDTO): UserAutoDeactivationAuditDTO {
    return item;
  }

  loadDeactivationSettings() {
    // load deactivation settings (organisation sub-types relevant to customers)
    this.customerService.getDeactivationSettings().then((result) => {
      this.deactivationSettings = result;

      //this.logger.trace('Got deactivation settings', this.deactivationSettings);
    });
  }

  onGridLazyLoad(event: LazyLoadEvent) {
    this.first = event.first;
    this.numberOfRows = event.rows;

    this.setPageData();
  }

  setPageData() {
    this.filteredData = this.usersData;

    if (this.selectedAuditType == AuditType.Warned)
      this.filteredData = this.filteredData.filter((x) => !!x.warnDate && !x.deactivationDate);
    if (this.selectedAuditType == AuditType.Deactivated)
      this.filteredData = this.filteredData.filter((x) => !!x.deactivationDate);

    if (this.searchCustomer)
      this.filteredData = this.filteredData.filter((x) =>
        x.organisationName.toLowerCase().includes(this.searchCustomer.toLowerCase()),
      );
    if (this.searchUser)
      this.filteredData = this.filteredData.filter(
        (x) =>
          x.userName.toLowerCase().includes(this.searchUser.toLowerCase()) ||
          x.userEmail.toLowerCase().includes(this.searchUser.toLowerCase()),
      );

    const start = this.first;
    const rows = this.numberOfRows;

    this.viewData = this.filteredData.slice(start, start + rows);
  }

  editSetting(item) {
    this.form.patchValue({
      accessDeactivateDays: item.accessDeactivateDays,
      accessWarnDays: item.accessWarnDays,
    });

    this.selectedSetting = item;
    this.showSettingDialog = true;
  }

  saveSetting(setting) {
    if (this.form.invalid) {
      return;
    }

    setting = { ...setting, ...this.form.value };

    this.showSettingDialog = false;

    this.customerService.saveDeactivationSettings(setting).then(() => {
      this.loadDeactivationSettings();
    });
  }

  reset() {
    this.searchCustomer = null;
    this.searchUser = null;
    this.selectedAuditType = AuditType.All;
    this.search();
  }

  search() {
    this.first = 0;
    this.setPageData();
  }

  onAuditTypeChanged() {
    this.first = 0;
    this.search();
  }

  getCRMCSV() {
    this.csvDownloading = true;
    this.customerService
      .generateCustomerUserCRMCSV()
      .then(() => {
        this.csvDownloading = false;
      })
      .catch((err) => {
        let errText = 'Could not export the csv.';
        if (err.status == 403) {
          errText = 'You do not have permission to export this data';
        }

        this.csvDownloading = false;
        this.messageService.add(Message.Error(errText));
      });
  }
}
